import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, ref } from 'vue';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import CButton from '@/components/shared/CButton/CButton.vue';
import CError from '@/components/shared/CError.vue';
import CInput from '@/components/shared/CInput/CInput.vue';
import CRecaptcha, { type CRecaptchaRef } from '@/components/shared/CRecaptcha';
import StoreFields from '@/components/Auth/StoreFields.vue';
import SocialAuthButton from '@/components/Auth/SocialAuthButton.vue';
import { completeExternalSignUp } from '@/api/auth';
import { registerUser } from '@/api/user';
import { externalAuthErrorMessage, toRegistrationStoreUrl } from '@/utils/authUtils';
import { toRegistrationErrorMessage } from '@/views/SignUp/registrationError';
import { appStoreListings, providerSignUps } from '@/views/SignUp/storeTypes';
import type { AxiosError } from 'axios';
import { externalProviderLabels, type ExternalAuthResult, type ExternalProvider } from '@/typings/auth/externalAuth';


export default /*#__PURE__*/_defineComponent({
  __name: 'SignUpForm',
  props: {
    storeType: null,
    lockStoreType: { type: Boolean },
    managedBy: null,
    prefilled: null,
    providerLoading: { type: Boolean }
  },
  emits: ["back", "update:storeType", "registered", "completed", "provider-sign-up"],
  setup(__props: any, { emit }: { emit: ({
    (event: 'back'): void;
    (event: 'update:storeType', storeType: string): void;
    (event: 'registered', username: string, storeType: string): void;
    (event: 'completed', result: ExternalAuthResult): void;
    (event: 'provider-sign-up', provider: ExternalProvider): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    storeType: string;
    lockStoreType?: boolean;
    /** Partner that manages the account (`mngBy` query param), sent along with the manual sign up. */
    managedBy?: string;
    /** Set when a provider already verified the identity: then the account is created from its ticket. */
    prefilled?: { provider: ExternalProvider; pendingTicket: string; email: string; username?: string };
    /** A provider sign up started from this form is running. */
    providerLoading?: boolean;
};





const prefilled = computed(() => props.prefilled);

// The page shows the pitch of the selected platform
const onStoreTypeChange = (storeType: string) => {
    form.storeType = storeType;
    emit('update:storeType', storeType);
};
const providerLabel = computed(() => externalProviderLabels[props.prefilled?.provider || 'google']);

const captchaToken = ref('');
const recaptchaRef = ref<CRecaptchaRef>();

const submitting = ref(false);
const errorMessage = ref('');
const form = reactive({
    storeType: props.storeType,
    storeUrl: '',
    name: '',
    username: props.prefilled?.username || '',
    email: props.prefilled?.email || '',
    password: '',
    confirmationPassword: ''
});

const appStoreListing = computed(() => appStoreListings[form.storeType]);
const providerSignUp = computed(() => providerSignUps[form.storeType]);
const providerName = computed(() => (providerSignUp.value ? externalProviderLabels[providerSignUp.value] : ''));
// The store comes from elsewhere: no store, account or password fields here
const hasOwnOnboarding = computed(() => !!appStoreListing.value || !!providerSignUp.value);

const completePrefilledSignUp = async (pendingTicket: string): Promise<boolean> => {
    try {
        emit(
            'completed',
            await completeExternalSignUp({
                pendingTicket,
                storeType: form.storeType,
                storeUrl: toRegistrationStoreUrl(form.storeType, form.storeUrl.trim()),
                companyName: form.name.trim(),
                username: form.username.trim()
            })
        );
        return true;
    } catch (error) {
        errorMessage.value = externalAuthErrorMessage(error, 'We could not create your account. Please try again.');
        return false;
    }
};

const registerManually = async (): Promise<boolean> => {
    try {
        await registerUser({
            Name: form.name.trim(),
            Username: form.username.trim(),
            Email: form.email.trim(),
            Password: form.password,
            ConfirmationPassword: form.confirmationPassword,
            StoreType: form.storeType,
            StoreUrl: form.storeUrl.trim(),
            Captcha: captchaToken.value,
            ManagedBy: props.managedBy
        });
        emit('registered', form.username.trim(), form.storeType);
        return true;
    } catch (error) {
        recaptchaRef.value?.reset();
        errorMessage.value = toRegistrationErrorMessage(error as AxiosError);
        return false;
    }
};

const submit = async () => {
    errorMessage.value = '';
    submitting.value = true;
    const callback = prefilled.value
        ? () => completePrefilledSignUp(prefilled.value!.pendingTicket)
        : registerManually;
    const created = await callback();
    // On success the page is leaving for Stripe and the ticket is spent: a second click would only fail
    if (!created) submitting.value = false;
};

return { __sfc: true,props, emit, prefilled, onStoreTypeChange, providerLabel, captchaToken, recaptchaRef, submitting, errorMessage, form, appStoreListing, providerSignUp, providerName, hasOwnOnboarding, completePrefilledSignUp, registerManually, submit, ValidationObserver, ValidationProvider, CButton, CError, CInput, CRecaptcha, StoreFields, SocialAuthButton }
}

})